import React, {
  useState,
  useRef,
  useEffect,
  useMemo
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import "./ChatInterface.css";
import { RightDetails, AgentDropdown } from "./ChatParts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mammoth from "mammoth";
import * as XLSX from "xlsx";

import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import Sidebarnew from "./NewUI/Sidebarnew";
import Navigationnew from "./NewUI/Navigationnew";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;


// ======================================================
// GLOBAL MESSAGE ANIMATION CACHE
// ======================================================

const animatedMessageCache = new Set();

// ======================================================
// TYPEWRITER MARKDOWN
// ======================================================

const TypewriterMarkdown = React.memo(
  ({ text, msgId, speed = 2 }) => {

    const [displayed, setDisplayed] =
      React.useState(() => {

        // Already animated before
        if (
          animatedMessageCache.has(msgId)
        ) {
          return text;
        }

        return "";
      });

    const intervalRef =
      React.useRef(null);

    const isAnimatingRef =
      React.useRef(false);

    React.useEffect(() => {

      // No text
      if (!text) return;

      // Already completed
      if (
        animatedMessageCache.has(msgId)
      ) {
        setDisplayed(text);
        return;
      }

      // Prevent duplicate intervals
      if (isAnimatingRef.current) {
        return;
      }

      isAnimatingRef.current = true;

      let currentIndex = 0;

      intervalRef.current =
        setInterval(() => {

          const chunk =
  Math.floor(Math.random() * 4) + 1;

currentIndex += chunk;

          // Completed
          if (
            currentIndex >= text.length
          ) {

            setDisplayed(text);

            animatedMessageCache.add(
              msgId
            );

            clearInterval(
              intervalRef.current
            );

            isAnimatingRef.current = false;

            return;
          }

          setDisplayed(
            text.slice(0, currentIndex)
          );

        }, speed);

      return () => {

        if (intervalRef.current) {
          clearInterval(
            intervalRef.current
          );
        }

        isAnimatingRef.current = false;
      };

    }, [text, msgId, speed]);

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
      >
        {displayed}
      </ReactMarkdown>
    );
  }
);

function ChatInterface() {
  const { agentId, shareToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isReadOnly = !!shareToken;
  const hasAutoSent = useRef(false);
  const [loggedInUserName, setLoggedInUserName] = useState("");
  
  // Add missing state for previewArtifact
  const [previewArtifact, setPreviewArtifact] = useState(null);

useEffect(() => {
  api.get("/profile").then(res => {
    setLoggedInUserName(res.data?.name || "");
  }).catch(() => {});
}, []);

  const effectiveAgentId = useMemo(() => {
    if (shareToken) {
      try {
        const decoded = atob(shareToken);
        return decoded === "undefined" ? null : decoded;
      } catch (e) {
        return null;
      }
    }
    return agentId;
  }, [shareToken, agentId]);

  const [activeSourceMsgId, setActiveSourceMsgId] = useState(null);
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [speakingMsgId, setSpeakingMsgId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); // New state for overall page loading
  const [currentConversationId, setCurrentConversationId] = useState(null);

  const [chatHistory, setChatHistory] = useState([]);
  const [agentCatalog, setAgentCatalog] = useState({});
  const [historySearchQuery, setHistorySearchQuery] = useState(""); // ✅ NEW: Chat history search state

  const [isOn, setIsOn] = useState(true); // Kept state to not break dependencies, but UI is removed
  const [showHistory, setShowHistory] = useState(true);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  useEffect(() => {
  setMessages([]);
  setCurrentConversationId(null);
  setSelectedFile(null);
  setAttachedDocs([]);
  setSmartArtifacts([]);
  setActiveSourceMsgId(null);
}, [effectiveAgentId]);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  // const [showRightPanel, setShowRightPanel] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareInput, setShareInput] = useState("");
  const [shareList, setShareList] = useState([]);
  const [shareMessage, setShareMessage] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [hoveredChatId, setHoveredChatId] = useState(null);

const [openMenuId, setOpenMenuId] = useState(null);
const [deleteConfirmGroup, setDeleteConfirmGroup] = useState(null);

  const AGENT_ICONS = {
    Finance: "/images/Finance.svg",
    Legal: "/images/legal 1.svg",
  };

  const [attachedDocs, setAttachedDocs] = useState([]);
  const [smartArtifacts, setSmartArtifacts] = useState([]);
  const [smartArtifactLoading, setSmartArtifactLoading] = useState(false);
  const [smartConversationIds, setSmartConversationIds] = useState([]);

  let workspaceId = "";
  workspaceId = localStorage.getItem("commonId");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  const [activeAgents, setActiveAgents] = useState([]);
  const [allWorkspaceAgents, setAllWorkspaceAgents] = useState([]);
  const allActiveAgentNames =
    activeAgents.length > 0 && activeAgents[0]?.name
      ? activeAgents.map((a) => a.name).join(", ")
      : "Loading Agent...";

  useEffect(() => {
    const loadInitialData = async () => {
      setPageLoading(true);
      try {
        // if (effectiveAgentId && !isReadOnly) {
        //   const res = await api.get(`/agents/${effectiveAgentId}`);
        //   const agentData = res.data;
        //   setActiveAgents([agentData]); // Ensure it's an array
        //   setCurrentAgent(agentData.id);
        // }

        if (!isReadOnly) {
          await fetchCatalog();
        }

        if (effectiveAgentId || shareToken) {
          await fetchChatData();
        }
      } catch (err) {
        console.error("Error loading initial chat data:", err);
        if (!isReadOnly) toast.error("Failed to load initial chat data");
      } finally {
        setPageLoading(false);
      }
    };

    const fetchCatalog = async () => { // Moved inside loadInitialData scope
      try {
        const res = await api.get("/agents"); // This is the call that fetches all agents for the catalog
        const agents = res.data;
        setAllWorkspaceAgents(agents);
        const newCatalog = {};
        agents.forEach((agent) => {
          const domain = agent.type || "Other";
          if (!newCatalog[domain]) {
            newCatalog[domain] = [];
          }
          newCatalog[domain].push({ id: agent.id, name: agent.name });
        });
        setAgentCatalog(newCatalog);
      } catch (err) {
        console.error("Error fetching agent catalog:", err);
        if (!isReadOnly) toast.error("Failed to fetch agent catalog");
      }
    };

   const fetchChatData = async () => { // Moved inside loadInitialData scope
  // This fetches docs and history for the current agent
  if (!currentAgent && !shareToken) return; // Only proceed if an agent is selected or shared

  try {
    if (isReadOnly) {
      const res = await api.get(`/public/share/${shareToken}/data`);

      setAttachedDocs(res.data.documents);
      setChatHistory(res.data.conversations);
      setActiveAgents([res.data.agent]);
      setCurrentAgent(res.data.agent.id);

      const docsList = res.data.documents || [];

      const hist = res.data.conversations
        .slice()
        .reverse()
        .flatMap((item) => {
          // ✅ FIX: Same logic for shared chats
          const msgLower = (item.question || "").toLowerCase().trim();
          const patterns = [
            "hi",
            "hello",
            "hey",
            "thanks",
            "thank you",
            "ok",
            "okay",
            "bye",
          ];

          const isGeneric = patterns.some(
            (p) => msgLower === p || msgLower.startsWith(p + " ")
          );

          let realSources = [];

          if (!isGeneric && docsList.length > 0) {
            realSources = docsList.map((doc) => ({
              name: doc.file_name || doc.name,
              url: "#",
            }));
          }

          return [
            {
              id: `q-${item.id}`,
              text: item.question,
              sender: "user",
            },
            {
              id: `a-${item.id}`,
              text: item.answer,
              sender: "agent",
              isHistory: true,
              messageId: item.id,
              sessionId: item.session_id,
              rating: item.rating,
              sourcesCount: realSources.length,
              sourcesList: realSources,

              pdfUrl:
                item.file_url &&
                (
                  item.file_url.toLowerCase().includes(".pdf") ||
                  item.file_url.includes("pdfs/")
                )
                  ? item.file_url
                  : null,

              imageUrl:
                item.file_url &&
                (
                  item.file_url.toLowerCase().includes(".png") ||
                  item.file_url.toLowerCase().includes(".jpg") ||
                  item.file_url.includes("images/")
                )
                  ? item.file_url
                  : null,
            },
          ];
        });

      setMessages(hist);
    } else {
      if (currentAgent) { // Only fetch if currentAgent is set
        const resDocs = await api.get(`/agents/${currentAgent}/documents`);
        setAttachedDocs(resDocs.data);

        const resHist = await api.get(
          `/agents/${currentAgent}/conversations?limit=1000`
        );

        const list =
          resHist.data.conversations ||
          (Array.isArray(resHist.data) ? resHist.data : []);

        setChatHistory(list);
      }
    }
  } catch (err) {
    console.error("Error fetching documents/history:", err);

    if (!isReadOnly) {
      toast.error("Failed to fetch chat history");
    }
  }
};
    loadInitialData();
  }, [effectiveAgentId, isReadOnly, shareToken]); // Depend on effectiveAgentId, isReadOnly, shareToken

  const [currentAgent, setCurrentAgent] = useState(effectiveAgentId); // Initialize with effectiveAgentId
 useEffect(() => {
    if (effectiveAgentId) {
      api
        .get(`/agents/${effectiveAgentId}`)
        .then((res) => {
          // Fix: Extract the first object since the backend returns an array (result.rows)
          const agentData = Array.isArray(res.data) ? res.data[0] : res.data;
          
          if (agentData) {
            setActiveAgents([agentData]);
            setCurrentAgent(agentData.id);
          }
        })
        .catch((err) => {
          console.error("Failed loading current agent", err);
        });
    }
  }, [effectiveAgentId]);

const groupedHistory = useMemo(() => {
  if (!chatHistory || !Array.isArray(chatHistory)) return [];

  const sorted = [...chatHistory].sort(
    (a, b) => new Date(a.created_at || Date.now()) - new Date(b.created_at || Date.now())
  );

  const grouped = {};

  sorted.forEach((item) => {
    const key = item.session_id || item.id;

    if (!grouped[key]) {
      grouped[key] = {
        session_id: key,
        groupTime: new Date(item.created_at),
        items: [],
        is_pinned: false,
        pinned_at: null,
      };
    }

    grouped[key].items.push(item);

    // ✅ If ANY row in the session is pinned, the whole group is pinned
    if (item.is_pinned === true) {
      grouped[key].is_pinned = true;
      if (item.pinned_at) grouped[key].pinned_at = item.pinned_at;
    }

    const itemTime = new Date(item.created_at || Date.now());
    if (itemTime > grouped[key].groupTime) {
      grouped[key].groupTime = itemTime;
    }
  });

  return Object.values(grouped).sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    if (a.is_pinned && b.is_pinned) {
     return new Date(a.pinned_at || 0)
     - new Date(b.pinned_at || 0);
    }
    return b.groupTime - a.groupTime;
  });
}, [chatHistory]);

  // ✅ to filter chats quickly
  const filteredHistory = useMemo(() => {
    if (!historySearchQuery.trim()) return groupedHistory;
    const lowerQuery = historySearchQuery.toLowerCase();
    
    return groupedHistory.reduce((acc, group) => {
      // Check if any question or answer in this group matches the search
      const match = group.items.find(item => 
        (item.question && item.question.toLowerCase().includes(lowerQuery)) ||
        (item.answer && item.answer.toLowerCase().includes(lowerQuery))
      );

      // If there is a match, keep the group and set a special display title
      if (match) {
        acc.push({
          ...group,
          displayTitle: match.question // Forces UI to show the matching question
        });
      }
      return acc;
    }, []);
  }, [groupedHistory, historySearchQuery]);

const handleHistoryClick = async (group) => {
  const historicalMessages = (
    await Promise.all(
      group.items.map(async (item) => {

        let realSources = [];

        try {
          // ✅ FIX: Prevent generic greetings from searching the DB and returning fake sources
          const msgLower = (item.question || "").toLowerCase().trim();
          const patterns = ["hi", "hello", "hey", "thanks", "thank you", "ok", "okay", "bye"];
          const isGeneric = patterns.some(
            (p) => msgLower === p || msgLower.startsWith(p + " ")
          );

          if (!isGeneric && attachedDocs.length > 0) {
            const res = await api.post(
              "/conversations/history-sources",
              {
                agentId: currentAgent,
                question: item.question
              }
            );

            // 🍒 CHERRY ON CAKE: Map ONLY the specific documents returned by the Vector DB!
            if (res.data.sources && res.data.sources.length > 0) {
              realSources = res.data.sources.map((s) => ({
                name: s.name || s,
                url: "#"
              }));
            }
          }
        } catch (err) {
          console.error(err);
        }

        return [
          {
            id: `q-${item.id}`,
            text: item.question,
            sender: "user",
            time: new Date(item.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit"
            }),
          },
          {
            id: `a-${item.id}`,
            text: item.answer,
            sender: "agent",
            isHistory: true,
            messageId: item.id,
            sessionId: item.session_id,
            rating: item.rating,
            sourcesCount: realSources.length,
            sourcesList: realSources,

            // ✅ FIX: Safely route the DB file URL to the correct image or PDF renderer
            pdfUrl:
              item.file_url &&
              (
                item.file_url.toLowerCase().includes(".pdf") ||
                item.file_url.includes("pdfs/")
              )
                ? item.file_url
                : null,

            imageUrl:
              item.file_url &&
              (
                item.file_url.toLowerCase().includes(".png") ||
                item.file_url.toLowerCase().includes(".jpg") ||
                item.file_url.includes("images/")
              )
                ? item.file_url
                : null,

            time: new Date(item.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit"
            }),
          }
        ];
      })
    )
  ).flat();

  setMessages(historicalMessages);
  setCurrentConversationId(group.session_id);

  // ✅ FIX: Use session_id for artifact fetching
  const artifactIds = [
    group.session_id,                      // ✅ Primary: session_id
    ...group.items.map((item) => item.id), // ✅ Fallback: individual row IDs
  ].filter(Boolean);

  setSmartConversationIds([...new Set(artifactIds)]);
  setSmartArtifacts([]);
};

// ============================================================
  // ✅ AUTO-LOAD PASSED CONVERSATION FROM HOME PAGE
  // ============================================================
 
  useEffect(() => {
    const hasInitialData = typeof location.state?.initialMessage === "string" || !!location.state?.initialFile;

    if (hasInitialData && currentAgent && !hasAutoSent.current && !pageLoading) {
      const initMsg = location.state.initialMessage || "";
      const initFile = location.state.initialFile || null;

      hasAutoSent.current = true;
      
      const newState = { ...location.state };
      delete newState.initialMessage;
      delete newState.initialFile;
      navigate(location.pathname, { replace: true, state: newState });

      // ✅ FIX: Do NOT set the file to the UI state here. Just pass it to handleSend directly!
      handleSend(initMsg, initFile);
    }
  }, [location.state, currentAgent, pageLoading, navigate]);

 // ============================================================
  // ✅ RESTORED: AUTO-LOAD HISTORY FROM SIDEBAR
  // ============================================================
  useEffect(() => {
    // Check if the sidebar passed a specific session to load
    const passedSessionId = location.state?.sessionId || location.state?.conversationId;

    if (passedSessionId && groupedHistory.length > 0 && !pageLoading) {
      // Find the specific history group that matches the passed ID
      const groupToLoad = groupedHistory.find(
        (g) => String(g.session_id) === String(passedSessionId)
      );

      // If we found it and it's not already open, load it!
      if (groupToLoad && String(currentConversationId) !== String(passedSessionId)) {
        handleHistoryClick(groupToLoad);
        
        // Clean up the location state so it doesn't re-trigger infinitely
        const newState = { ...location.state };
        delete newState.sessionId;
        delete newState.conversationId;
        navigate(location.pathname, { replace: true, state: newState });
      }
    }
  }, [location.state, groupedHistory, pageLoading, currentConversationId, navigate]);
const handlePinChat = async (group) => {
  const isPinning = !group.is_pinned;
  try {
    setChatHistory((prev) => 
      prev.map((item) => 
        (item.session_id || item.id) === group.session_id 
          ? { ...item, is_pinned: isPinning, pinned_at: isPinning ? new Date().toISOString() : null }
          : item
      )
    );
    setOpenMenuId(null);

    await api.patch(`/conversations/${group.session_id}/pin`, {
      isPinned: isPinning
    });

    // ✅ Success toast
   toast.success(isPinning ? "Chat pinned successfully" : "Chat unpinned successfully", {
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: true,
  style: {
    background: "#FAFAFA",
    color: "#1783ef",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #2C2C2E",
    marginTop: "70px",  // ✅ pushes below navbar
    marginRight: "16px"
  }
});

    if (currentAgent) {
      const resHist = await api.get(`/agents/${currentAgent}/conversations?limit=1000`);
      const list = resHist.data.conversations || (Array.isArray(resHist.data) ? resHist.data : []);
      setChatHistory(list);
    }

  } catch (err) {
    console.error(err);
    // Rollback optimistic update
    if (currentAgent) {
      const resHist = await api.get(`/agents/${currentAgent}/conversations?limit=1000`);
      setChatHistory(resHist.data.conversations || []);
    }
    toast.error(err?.response?.data?.message || "Failed to pin chat", {
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: true,
    });
  }
};

const handleDeleteChat = async (group) => {
  try {
    setChatHistory((prev) => prev.filter((item) => 
      (item.session_id || item.id) !== group.session_id
    ));
    setOpenMenuId(null);

    if (String(currentConversationId) === String(group.session_id)) {
      setMessages([]);
      setCurrentConversationId(null);
      setSmartArtifacts([]);
      setSmartConversationIds([]);
    }

    await api.delete(`/conversations/${group.session_id}`);

    // ✅ Success toast
   toast.success("Chat deleted successfully", {
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: true,
  style: {
    background: "#FAFAFA",
    color: "#1783ef",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #2C2C2E",
    marginTop: "70px",
    marginRight: "16px"
  }
});

    if (currentAgent) {
      const resHist = await api.get(`/agents/${currentAgent}/conversations?limit=1000`);
      const list = resHist.data.conversations || (Array.isArray(resHist.data) ? resHist.data : []);
      setChatHistory(list);
    }

  } catch (err) {
    console.error(err);
    toast.error("Failed to delete chat", {
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: true,
    });
    if (currentAgent) {
      const resHist = await api.get(`/agents/${currentAgent}/conversations?limit=1000`);
      setChatHistory(resHist.data.conversations || []);
    }
  }
};

// Helper to reduce repetition
const buildMessages = (item, sourcesUsedCount) => [
  {
    id: `q-${item.id}`,
    text: item.question,
    sender: "user",
    time: new Date(item.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  },
  {
    id: `a-${item.id}`,
    text: item.answer,
    sender: "agent",
    isHistory: true,
    conversationId: item.id,
    rating: item.rating,
    sourcesCount: sourcesUsedCount,
    time: new Date(item.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  },
];

  const handleNewChat = () => {
    setMessages([]);
setCurrentConversationId(null);
    setMessage("");
    setSelectedFile(null);
    setIsTyping(false);
    setActiveSourceMsgId(null);
    setCopiedMsgId(null);
    setSpeakingMsgId(null);
    setPreviewFile(null);
    setSmartConversationIds([]);
    setSmartArtifacts([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
  const closePopup = () => {
    setActiveSourceMsgId(null);
  };

  document.addEventListener("click", closePopup);

  return () => {
    document.removeEventListener("click", closePopup);
  };
}, []);

// ✅ REPLACE your closeMenu useEffect with this
useEffect(() => {
  const closeMenu = (e) => {
    // Don't close if clicking inside a dropdown menu
    if (e.target.closest('.history-dropdown-menu') || 
        e.target.closest('.history-item-menu')) {
      return;
    }
    setOpenMenuId(null);
  };

  document.addEventListener("mousedown", closeMenu);
  return () => document.removeEventListener("mousedown", closeMenu);
}, []);

  const [userType] = useState(() => {
    const stored = localStorage.getItem("userType")?.toLowerCase();
    return stored === "company" || stored === "individual" ? stored : "individual";
  });

  const companyName = userType === "company" ? localStorage.getItem("companyName") || "—" : "Individual";

  const currentAgentDetails = activeAgents.find((a) => String(a.id) === String(currentAgent)) || activeAgents[0] || null;
  const switchableAgents =
  allWorkspaceAgents.filter((agent) => {

    const isActive =
      agent.status === "Active" ||
      parseInt(agent.doc_count || 0) > 0;

    return (
      isActive &&
      String(agent.id) !==
      String(currentAgent)
    );
  });
// This joins all active agent names with a " + "
// const allActiveAgentNames = activeAgents.map(a => a.name).join(" + ") || "Agent";
  const currentAgentDomain = currentAgentDetails?.domain || currentAgentDetails?.type || "Finance";
  const currentAgentIcon = AGENT_ICONS[currentAgentDomain] || "/images/Finance.svg";

  const getDocumentNameFromUrl = (url, fallback = "Generated document") => {
    if (!url) return fallback;
    try {
      const cleanPath = new URL(url, window.location.origin).pathname;
      const lastPart = decodeURIComponent(cleanPath.split("/").filter(Boolean).pop() || "");
      return lastPart || fallback;
    } catch (err) {
      const lastPart = String(url).split("/").filter(Boolean).pop();
      return lastPart ? decodeURIComponent(lastPart.split("?")[0]) : fallback;
    }
  };

  const getFileExtension = (name = "") => {
    const match = String(name).match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toUpperCase() : "DOC";
  };

  // const openGeneratedPreview = ({ url, type = "document", name }) => {
  //   if (!url) return;
  //   setPreviewFile({
  //     url,
  //     type,
  //     name: name || getDocumentNameFromUrl(url),
  //   });
  // };

  const handleSpeak = (text, msgId) => {
    if (!text) return;
    if (speakingMsgId === msgId) {
      window.speechSynthesis.cancel();
      setSpeakingMsgId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const speech = new SpeechSynthesisUtterance(text);
    speech.lang = "en-US";
    speech.onend = () => setSpeakingMsgId(null);
    speech.onerror = () => setSpeakingMsgId(null);
    setSpeakingMsgId(msgId);
    window.speechSynthesis.speak(speech);
  };

  const handleCopy = (text, msgId) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedMsgId(msgId);
        setTimeout(() => setCopiedMsgId(null), 2000);
      })
      .catch((err) => console.error(err));
  };

  const handleExportSummary = (text) => {
    downloadFile(text, `summary-${Date.now()}.txt`);
  };

  const handleCreateReport = (text) => {
    downloadFile(`REPORT\n\nDate: ${new Date().toLocaleString()}\n\n${text}`, `report-${Date.now()}.txt`);
  };

  const handleRating = async (msgId, conversationId, newRating) => {
    if (isReadOnly) return;
    try {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === msgId ? { ...msg, rating: newRating } : msg
        )
      );
      if (conversationId) {
        await api.put(`/chat/${conversationId}/rating`, { rating: newRating });
      }
    } catch (err) {
      console.error("Failed to update rating", err);
      toast.error("Failed to update rating");
    }
  };

 const handleDownloadPdf = (fileUrl, fileName = "document.pdf") => {
  try {
    const link = document.createElement("a");

    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  } catch (err) {
    console.error("Download failed", err);
  }
};


const extractPdfText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const pdf = await pdfjsLib.getDocument({
    data: uint8Array,
  }).promise;

  let text = "";

  for (let i = 1; i <= pdf.numPages; i++) {

    const page = await pdf.getPage(i);

    const content =
      await page.getTextContent();

    const strings = content.items.map(
      (item) => item.str
    );

    text += strings.join(" ") + "\n";
  }

  return text;
};
const extractTextFromFile = async (file) => {
  if (!file) return "";

  const fileName = file.name.toLowerCase();

  // ✅ FIX: Treat SVG files as plain text so the AI can read their XML/Vector data
  if (fileName.endsWith(".txt") || fileName.endsWith(".svg")) {
    return await file.text();
  }

  // PDF
if (fileName.endsWith(".pdf")) {
  return await extractPdfText(file);
}

  // DOCX
  if (fileName.endsWith(".docx")) {
    const arrayBuffer = await file.arrayBuffer();

    const result = await mammoth.extractRawText({
      arrayBuffer,
    });

    return result.value;
  }

  // XLSX / XLS
  if (
    fileName.endsWith(".xlsx") ||
    fileName.endsWith(".xls")
  ) {
    const arrayBuffer = await file.arrayBuffer();

    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
    });

    let text = "";

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
      });

      text += `\nSheet: ${sheetName}\n`;

      rows.forEach((row) => {
        text += row.join(" | ") + "\n";
      });
    });

    return text;
  }

  return "";
};
const handleSend = async (overrideMessage = null, overrideFile = null) => {
  // 1. Determine text
  const textToSend = typeof overrideMessage === "string" ? overrideMessage : message;

  // Use overrideFile if passed, otherwise use selectedFile from state
  const fileToUse = overrideFile || selectedFile;

  if (isReadOnly || (!textToSend.trim() && !fileToUse)) return;

  // 2. DUPLICATE PROTECTION
  if (overrideMessage && messages.some(m => m.text === overrideMessage)) {
    return;
  }

  // ✅ FIX: Format bubble text to include the filename if a file exists
  let bubbleText = textToSend;
  if (fileToUse?.file?.name) {
    bubbleText = textToSend.trim() 
      ? `${textToSend}\n\n📎 ${fileToUse.file.name}` 
      : `📎 ${fileToUse.file.name}`;
  }

  const userMessage = {
    id: Date.now(),
    text: bubbleText, // Now shows the attachment label
    sender: "user",
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };

  // 3. Update state & Clear UI IMMEDIATELY
  setMessages((prev) => [...prev, userMessage]);
  setMessage("");            // Clear text box
  setSelectedFile(null);     // Clear attachment pill from UI
  setIsTyping(true);
  
  if (fileInputRef.current) {
    fileInputRef.current.value = ""; // Reset hidden file input
  }

  const msgText = textToSend;

  try {
    let activeSessionId = currentConversationId;

    if (!activeSessionId) {
      activeSessionId = crypto.randomUUID();
      setCurrentConversationId(activeSessionId);
    }

    let extractedText = "";

    // Extract file text 
    if (fileToUse?.file) {
      try {
        extractedText = await extractTextFromFile(fileToUse.file);
        console.log("✅ Extracted text length:", extractedText?.length || 0);
        console.log("✅ Preview:", extractedText?.slice(0, 200));
      } catch (extractErr) {
        console.error("❌ File extraction failed:", extractErr);
        toast.error("Could not read file content. Please try again.");
      }
    }

    // Final AI message
    let finalMessage = "";

    if (extractedText) {
      finalMessage += `\n\nUploaded File Content:\n${extractedText}`;
    }

    const payload = {
      message: msgText,
      uploaded:finalMessage,
      workspaceId,
      conversationId: activeSessionId,
    };

    if (activeAgents.length > 1) {
      payload.agentIds = activeAgents.map((a) => a.id);
    }

    let res;

    if (activeAgents.length > 1) {
      res = await api.post(`/agents/multi-chat-mcp`, payload);
    } else {
      // Strip out literal "undefined" string if it leaked into the URL
      let targetAgentId = (agentId && agentId !== "undefined") ? agentId : currentAgent;
      if (targetAgentId === "undefined") targetAgentId = null;

      if (!targetAgentId) {
        toast.error("No agent selected");
        setIsTyping(false);
        return;
      }

      res = await api.post(`/agents/${targetAgentId}/chat-mcp`, payload);
    }

    const {
      answer,
      sources = [],
      pdfUrl,
      imageUrl,
      conversationId,
      messageId,
    } = res.data;

    // ✅ FIX: Determine if the message is a basic greeting
    const msgLower = msgText.toLowerCase().trim();
    const patterns = ["hi", "hello", "hey", "thanks", "thank you", "ok", "okay", "bye"];

    const isGeneric = patterns.some(
      (p) => msgLower === p || msgLower.startsWith(p + " ")
    );

    // 🍒 CHERRY ON CAKE: Use ONLY the exact sources the vector DB actually scanned!
    const finalSourcesList = (!isGeneric && sources && sources.length > 0)
      ? sources.map((s) => ({
          name: s.name || s,
          url: "#",
        }))
      : [];

    if (conversationId) {
      setCurrentConversationId(conversationId);
    }

    const botMessage = {
      id: Date.now() + 1,
      text: answer,
      data: null,
      sender: "agent",
      sourcesCount: finalSourcesList.length,
      sourcesList: finalSourcesList,
      pdfUrl: pdfUrl || null,
      imageUrl: imageUrl || null,
      sessionId: conversationId,
      messageId,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, botMessage]);

  } catch (err) {
    console.error(err);
    toast.error("Chat failed");
  } finally {
    setIsTyping(false);
  }
};

  const handleShareClick = () => {
    const targetId = agentId || currentAgent; 
    if (!targetId) return toast.error("Select an agent first");
    
    const token = btoa(targetId); 
    const shareUrl = `${window.location.origin}/share-chat/${token}`;
    
    setShareMessage(shareUrl);
    setShowShareModal(true);
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard!");
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchSmartArtifacts = async () => {
    if (!smartConversationIds.length) {
      setSmartArtifacts([]);
      return;
    }
    try {
      setSmartArtifactLoading(true);
      const responses = await Promise.all(
        smartConversationIds.map((id) => api.get(`/artifacts/conversation/${id}`))
      );
      const allArtifacts = responses.flatMap((res) => res.data || []);
      setSmartArtifacts(allArtifacts);
    } catch (err) {
      console.error("Failed to fetch smart artifacts:", err);
      setSmartArtifacts([]);
    } finally {
      setSmartArtifactLoading(false);
    }
  };

  // useEffect(() => {
  //   if (showRightPanel) {
  //     fetchSmartArtifacts();
  //   }
  // }, [showRightPanel, smartConversationIds]);

  // ============================================================
  // ✅ AUTO-START CHAT FROM HOME PAGE PROMPT
  // ============================================================
// ============================================================
  // ✅ AUTO-START CHAT FROM HOME PAGE PROMPT (FIXED)
  // ============================================================
 

  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const getDateLabel = (dateVal) => {
    const d = new Date(dateVal);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  };

  const historyByDateLabel = useMemo(() => {
  const map = {};
  groupedHistory.forEach((group) => {
    const label = getDateLabel(group.groupTime);
    if (!map[label]) map[label] = [];
    map[label].push(group);
  });
  return map;
}, [groupedHistory]);

  // ============================================================================
  // BRAND NEW UI / RENDER METHODS - LIGHT THEME 100% MATCH
  // ============================================================================

  return (
    <>
      <style>
        {`
          @font-face {
            font-family: "Anthropic Serif";
            src: url(https://assets-proxy.anthropic.com/claude-ai/v2/assets/v1/c66fc489e-C-BHYa_K.woff2) format("woff2");
            font-weight: 300 800;
            font-style: normal;
            font-display: swap;
          }
         .chat-layout-wrapper {
  font-family: "Inter", sans-serif;
}
        `}
      </style>
    <div className="chat-layout-wrapper">

    {/* ─────────────────────────────────────────────────────────
         LEFT SIDEBAR (HISTORY & SEARCH)
      ────────────────────────────────────────────────────────── */}
      {!isReadOnly && <Sidebarnew
  selectedAgent={
    currentAgent
      ? {
          id: currentAgent,
          name:
            currentAgentDetails?.name || ""
        }
      : null
  }

  deleteConfirmGroup={
    deleteConfirmGroup
  }

  setDeleteConfirmGroup={
    setDeleteConfirmGroup
  }

  handleDeleteChat={
    handleDeleteChat
  }
/>}

      {/* ─────────────────────────────────────────────────────────
          MAIN CHAT AREA
      ────────────────────────────────────────────────────────── */}
    <div
  className={`chat-main-area ${
    artifactsOpen ? "artifacts-open" : ""
  }`}
>
        {/* Top Header */}
        {pageLoading ? (
          <header className="chat-main-header placeholder-glow">
            <div className="header-left d-flex align-items-center gap-3">
              {!isReadOnly && <div className="back-arrow-btn placeholder bg-secondary rounded-circle" style={{ width: '32px', height: '32px' }}></div>}
              <div className="agent-selector-wrapper d-flex align-items-center gap-2">
                <div className="agent-icon-box placeholder bg-secondary rounded-circle" style={{ width: '32px', height: '32px' }}></div>
                <span className="agent-name-text placeholder bg-secondary col-4"></span>
              </div>
            </div>
            <div className="header-right d-flex align-items-center gap-2">
              {!isReadOnly && <div className="artifacts-toggle-btn placeholder bg-secondary rounded-circle" style={{ width: '32px', height: '32px' }}></div>}
              {!isReadOnly && <div className="share-btn-outline placeholder bg-secondary col-2" style={{ height: '32px' }}></div>}
            </div>
          </header>
       ) : isReadOnly ? (
          <header className="chat-main-header">
            <div className="header-left">
              <div className="agent-selector-wrapper">
                <div className="agent-selector-btn">
                  <div className="agent-icon-box"><img src="/images/chatting.svg" alt="Chatting" /></div>
                  <span className="agent-name-text">{allActiveAgentNames}</span>
                </div>
              </div>
            </div>
          </header>
        ) : (
<Navigationnew
  selectedAgent={currentAgentDetails}
  remainingActiveAgents={switchableAgents}
  currentConversationId={currentConversationId}
  artifactsOpen={artifactsOpen}
  setArtifactsOpen={setArtifactsOpen}
  activeAgents={activeAgents}              
  setActiveAgents={setActiveAgents}        
  setCurrentAgent={setCurrentAgent}       
  currentAgent={currentAgent}             
  allWorkspaceAgents={allWorkspaceAgents}  
/>
        )}

        {/* Scrollable Chat Feed */}
        <div className="chat-feed-scrollable" ref={messagesContainerRef}>
          {pageLoading ? (
            <div className="chat-messages-wrapper placeholder-glow">
              <div className="message-row">
                <div className="message-bot">
                  <div className="bubble-bot placeholder col-8"></div>
                  <div className="bot-action-bar d-flex gap-2 mt-2">
                    <div className="placeholder col-3" style={{ height: '20px' }}></div>
                    <div className="placeholder col-2" style={{ height: '20px' }}></div>
                  </div>
                </div>
              </div>
              <div className="message-row">
                <div className="message-user">
                  <div className="bubble-user placeholder col-6"></div>
                </div>
              </div>
              <div className="message-row">
                <div className="message-bot">
                  <div className="bubble-bot placeholder col-9"></div>
                </div>
              </div>
            </div>
          ) : messages.length === 0 ? (
 <div className="chat-empty-state">
  {(() => {
    const h = new Date().getHours();
    const greet = h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
    return (
      <div className="chat-greeting-block">
        <img src="/images/Xlogo.svg" alt="KINEXAR" className="chat-greeting-logo" />
        <h2 className="chat-greeting-text">
          {greet}{loggedInUserName ? `, ${loggedInUserName}` : ""}
        </h2>
      </div>
    );
  })()}
</div>
          ) : (
            <div className="chat-messages-wrapper">
              {messages.map((msg) => (
                <div key={msg.id} className="message-row">
  {msg.sender === "user" ? (
                   <div className="user-message-wrapper">
  <div className="bubble-user" style={{ whiteSpace: 'pre-wrap' }}>
    {msg.text}
  </div>

  <div className="message-time user-time">
    {msg.time}
  </div>
</div>
                  ) : (
                  <div className="message-bot">

  {/* ✅ BOT HEADER WITH X LOGO */}
  <div className="bot-message-header">
    <img
      src="/images/Xlogo.svg"
      alt="X"
      className="bot-x-logo"
    />

    <span className="bot-name">
      {activeAgents?.[0]?.name || "KINEXAR"}
    </span>
  </div>

  {/* ✅ BOT RESPONSE CONTENT */}
  <div className="bubble-bot">

    {msg.data ? (

      <div className="bot-structured-response">
        <h6 className="response-title">
          {msg.data.title}
        </h6>

        {msg.data.sections.map((sec, i) => (
          <div
            key={i}
            className="response-section"
          >
            {sec.heading && (
              <div className="section-heading">
                {sec.heading}
              </div>
            )}

            <p className="section-content">
              {sec.content}
            </p>
          </div>
        ))}
      </div>

    ) : (

      <div className="bot-text-response ai-markdown-response">

        {!msg.isHistory ? (

          <TypewriterMarkdown
            msgId={msg.id}
            text={msg.text}
          />

        ) : (

          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
          >
            {msg.text}
          </ReactMarkdown>

        )}

      </div>

    )}

    {/* IMAGE */}
    {msg.imageUrl && (
      <div className="bot-attachment-img mt-3">
        <img
          src={msg.imageUrl}
          alt="Generated"
          onClick={() =>
            setPreviewFile({
              url: msg.imageUrl,
              type: "image",
              name: "Generated Image",
            })
          }
        />
      </div>
    )}

    {/* PDF */}
    {msg.pdfUrl && (
      <div className="bot-attachment-pdf mt-3">
        <button
          className="pdf-download-btn"
          onClick={() =>
            handleDownloadPdf(msg.pdfUrl)
          }
        >
          <i className="bi bi-download me-2"></i>
          Download PDF
        </button>
      </div>
    )}

  </div>

  {/* ✅ BOT TIMESTAMP */}
 

  {/* ✅ BOT ACTION BAR */}
  <div className="bot-action-bar position-relative">

    <div className="action-bar-left d-flex align-items-center gap-3">

      {/* Confidence */}
      {msg.sourcesCount > 0 && (
        <span
          className={`confidence-badge ${
            msg.confidence?.toLowerCase() ||
            "high"
          }`}
        >
          Confidence: {msg.confidence || "High"}
        </span>
      )}

      {/* Sources */}
     {/* Sources */}
      {msg.sourcesCount > 0 ? (
        <div
          className="sources-hover-wrapper"
          onMouseEnter={() => setActiveSourceMsgId(msg.id)}
          onMouseLeave={() => setActiveSourceMsgId(null)}
        >
          <span className="sources-link">
            Data found in <strong>{msg.sourcesCount || 0} sources</strong>
          </span>

          {/* BEAUTIFUL POPUP MODAL */}
          {activeSourceMsgId === msg.id && msg.sourcesList?.length > 0 && (
            <div className="sources-hover-popup">
              <div className="sources-popup-header">
                <strong>{msg.sourcesCount || 0} Documents Linked</strong>
                <i className="bi bi-x-lg" style={{cursor: "pointer", color: "#9CA3AF"}} onClick={() => setActiveSourceMsgId(null)}></i>
              </div>
              <div className="sources-popup-body">
                {msg.sourcesList?.map((doc, i) => (
                  <div key={i} className="source-item" onClick={() => navigate("/kb")}>
                    <span className="source-name" title={doc.name}>
                      <i className="bi bi-file-earmark-text me-2" style={{color: "#6B7280"}}></i>
                      {doc.name}
                    </span>
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "14px" }}></i>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <span className="sources-link-empty">
          Data found in <strong>0 sources</strong>
        </span>
      )}

      {/* STAR */}
      {msg.sourcesCount > 0 && (
        <StarRating
          value={msg.rating || 0}
          onChange={(val) =>
            handleRating(
              msg.id,
              msg.messageId,
              val
            )
          }
        />
      )}
    </div>

    {/* RIGHT ACTIONS */}
    <div className="action-bar-right d-flex gap-2">

      <button
        className="icon-action-btn"
        onClick={() =>
          handleSpeak(msg.text, msg.id)
        }
      >
        <i
          className={`bi ${
            speakingMsgId === msg.id
              ? "bi-stop-circle-fill text-danger"
              : "bi-volume-up"
          }`}
        ></i>
      </button>

      <button
        className="icon-action-btn"
        onClick={() =>
          handleCopy(msg.text, msg.id)
        }
      >
        <i
          className={`bi ${
            copiedMsgId === msg.id
              ? "bi-check-lg text-success"
              : "bi-clipboard"
          }`}
        ></i>
      </button>

    </div>
  </div>

  {/* GENERATE ACTIONS */}
  <div className="bot-generate-actions">

    <button
      className="generate-btn"
      onClick={() =>
        handleCreateReport(msg.text)
      }
    >
      <i className="bi bi-plus"></i>
      Create Report
    </button>

    <button
      className="generate-btn"
      onClick={() =>
        handleExportSummary(msg.text)
      }
    >
      <i className="bi bi-plus"></i>
      Export as Summary
    </button>

  </div>
{/* ✅ BOT TIMESTAMP */}
<div className="message-time bot-time">
  {msg.time}
</div>
</div>
                  )}
                </div>
              ))}
              
             
{isTyping && (
  <div className="message-row">
    <div className="message-bot">
      <div className="bubble-bot typing-bubble modern-loader-bubble">
        <TypingIndicator />
      </div>
    </div>
  </div>
)}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

     {/* Input Area */}
        {!isReadOnly && (
          <div className="chat-input-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* ✅ NEW: REAL ATTACHMENT DISPLAY PILL */}
           

 <div className="chat-input-pill shadow-sm">

  {/* FILE PREVIEW */}
  {selectedFile && (
    <div
      className="attached-file-pill"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "#F3F4F6",
        padding: "6px 12px",
        borderRadius: "8px",
        width: "fit-content",
        border: "1px solid #E5E7EB"
      }}
    >
      <span
        style={{
          fontSize: "13px",
          color: "#374151",
          fontWeight: "500",
          maxWidth: "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        📎 {selectedFile.file.name}
      </span>

      <button
        onClick={() => {
          setSelectedFile(null);

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }}
        style={{
          background: "none",
          border: "none",
          color: "#9CA3AF",
          cursor: "pointer",
          padding: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
          lineHeight: "1"
        }}
      >
        ✕
      </button>
    </div>
  )}

  {/* INPUT ROW */}
  <div className="chat-input-row">

    {/* ATTACH BUTTON */}
    <button
      className="input-attach-btn"
      onClick={() => fileInputRef.current.click()}
    >
      <img
        src="/images/cloudattach.svg"
        alt="attach"
        style={{
          width: "20px",
          height: "20px"
        }}
      />
    </button>

    {/* HIDDEN FILE INPUT */}
    <input
      type="file"
      ref={fileInputRef}
      className="d-none"
      onChange={(e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        setSelectedFile({
          file,
          preview:
            file.type &&
            file.type.startsWith("image/")
              ? URL.createObjectURL(file)
              : null,
        });
      }}
    />

    {/* TEXT INPUT */}
    <input
      type="text"
      className="chat-input-field"
      placeholder={
        selectedFile
          ? ""
          : "Search for the documents details by prompting here"
      }
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleSend();
        }
      }}
    />

    {/* MIC BUTTON */}
    <button className="input-mic-btn">
      <i className="bi bi-mic"></i>
    </button>

    {/* SEND BUTTON */}
   {/* REPLACE WITH THIS */}
<button
  className={`input-send-btn ${
    (!message.trim() && !selectedFile)
      ? "disabled"
      : ""
  }`}
  onClick={() => handleSend()}
  disabled={!message.trim() && !selectedFile}
>
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
</button>

  </div>
</div>
            <p style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#9CA3AF',
              margin: '8px 0 0 0',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
            }}>
              Kinexar AI can make mistakes. Verify important information.
            </p>
          </div>
        )}
      </div>   {/* ←←← CLOSE chat-main-area */}  {/* ←←← CLOSE chat-main-area */}

      {/* RIGHT SIDEBAR (GENERATED ARTIFACTS ONLY) */}
      {/* {showRightPanel && (
        <aside className="chat-sidebar-right shadow-sm">
          <div className="right-sidebar-header">
            <div className="flex-grow-1"></div>
            <button 
              className="close-right-btn" 
              onClick={() => setShowRightPanel(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          
          <div className="right-sidebar-content">
            {smartArtifactLoading ? (
              <div className="material-list placeholder-glow">
                <h6 className="material-list-title placeholder col-8"></h6>
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="material-name placeholder bg-secondary col-8"></div>
                ))}
              </div>
            ) : smartArtifacts.length > 0 ? (
              <div className="material-list">
                <h6 className="material-list-title">Generated Materials</h6>
                {smartArtifacts.map((item) => {
                  const itemName = item.file_name || getDocumentNameFromUrl(item.file_url, "Generated Artifact");
                  const itemType = item.type === "image" ? "image" : "document";

                  return (
                    <div
                      key={item.id}
                      className="material-card"
                      onClick={() =>
                        openGeneratedPreview({
                          type: itemType,
                          url: item.file_url,
                          name: itemName,
                        })
                      }
                    >
                      <div className="material-preview">
                        {itemType === "image" ? (
                          <img src={item.file_url} alt={itemName} />
                        ) : (
                          <>
                            <iframe src={item.file_url} title={`${itemName} preview`} />
                            <span>{getFileExtension(itemName)}</span>
                          </>
                        )}
                      </div>
                      <div className="material-info">
                        <span className="material-type">{itemType === "image" ? "Image" : "Document"}</span>
                        <div className="material-name" title={itemName}>
                          {itemName}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="material-download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPdf(item.file_url, itemName);
                        }}
                        title="Download"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="material-empty-state">
                <i className="bi bi-folder-x display-4 mb-3 text-muted"></i>
                <p className="m-0 fs-6 text-secondary">No material generated</p>
              </div>
            )}

            <div className="d-none">
              <RightDetails messages={messages} setPreviewFile={setPreviewFile} />
            </div>
          </div>
        </aside>
      )} */}

      {/* ─────────────────────────────────────────────────────────
          MODALS & OVERLAYS
      ────────────────────────────────────────────────────────── */}
           {/* MODALS & OVERLAYS */}
          {/* ─────────────────────────────────────────────────────────
          MODALS & OVERLAYS
      ────────────────────────────────────────────────────────── */}
      {previewFile && (
        <div className="cx-preview-overlay" onClick={() => setPreviewFile(null)}>
          <div className="cx-preview-modal shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <strong>{previewFile.name || "Preview"}</strong>
              <div className="preview-modal-actions">
                <button
                  type="button"
                  className="preview-icon-btn"
                  onClick={() => handleDownloadPdf(previewFile.url, previewFile.name || "document")}
                  title="Download"
                >
                  <i className="bi bi-download"></i>
                </button>
                <button
                  type="button"
                  className="preview-icon-btn"
                  onClick={() => setPreviewFile(null)}
                  title="Close"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
            {previewFile.type === "image" ? (
              <img src={previewFile.url} className="cx-preview-image" alt="Preview" />
            ) : (
              <iframe src={previewFile.url} title="PDF Preview" className="cx-preview-frame" />
            )}
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="cx-share-overlay" onClick={() => setShowShareModal(false)}>
          <div className="cx-share-modal shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold m-0 fs-5">Share Chat</h6>
              <button className="btn btn-sm btn-light rounded-circle" onClick={() => setShowShareModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <label className="form-label text-muted small fw-medium">Share this link with trusted contacts:</label>
            <div className="border rounded-3 p-3 bg-light mb-4 text-break user-select-all">
              <span className="small text-dark">{shareMessage}</span>
            </div>
            <div className="d-flex justify-content-end mt-2">
              <button className="btn btn-primary px-4 py-2 rounded-3 fw-medium" onClick={() => setShowShareModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmGroup && (
        <div className="cx-share-overlay" onClick={() => setDeleteConfirmGroup(null)}>
          <div className="cx-share-modal shadow-lg" onClick={(e) => e.stopPropagation()} style={{ width: '380px' }}>
            <h6 className="fw-bold mb-2 fs-5">Delete chat?</h6>
            <p className="text-muted mb-1" style={{ fontSize: '14px' }}>
              This will permanently delete{' '}
              <strong>{deleteConfirmGroup.items?.[0]?.question || 'this conversation'}</strong>.
            </p>
            <p className="text-muted mb-4" style={{ fontSize: '13px' }}>This action cannot be undone.</p>
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-sm px-4 py-2 rounded-3 fw-medium"
                style={{ border: '1px solid #D1D5DB', background: '#fff', color: '#374151' }}
                onClick={() => setDeleteConfirmGroup(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm px-4 py-2 rounded-3 fw-medium"
                style={{ background: '#EF4444', color: '#fff', border: 'none' }}
                onClick={() => {
                  handleDeleteChat(deleteConfirmGroup);
                  setDeleteConfirmGroup(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>   {/* ← Closes .chat-layout-wrapper */}
    {/* REMOVED THE EXTRA CODE AT THE BOTTOM THAT WAS CAUSING ISSUES */}
  </>
);
}

// Subcomponents preserved directly from original code
// REMOVED duplicate Typewriter component since TypewriterMarkdown already exists

const TypingIndicator = () => (
  <div className="modern-thinking-loader">
    <div className="thinking-ring"></div>
    <div className="thinking-ring delay1"></div>
    <div className="thinking-ring delay2"></div>

    <style>{`
      .modern-thinking-loader {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 4px;
      }

      .thinking-ring {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: linear-gradient(135deg, #3b82f6, #8b5cf6);
        animation: pulse 1.2s infinite ease-in-out;
        box-shadow: 0 0 10px rgba(59,130,246,0.5);
      }

      .thinking-ring.delay1 {
        animation-delay: 0.2s;
      }

      .thinking-ring.delay2 {
        animation-delay: 0.4s;
      }

      @keyframes pulse {
        0%, 100% {
          transform: scale(0.7);
          opacity: 0.5;
        }

        50% {
          transform: scale(1.2);
          opacity: 1;
        }
      }
    `}</style>
  </div>
);

const StarRating = ({ value = 0, onChange }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="d-flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`bi ${
            star <= (hover || value) ? "bi-star-fill text-warning" : "bi-star text-muted"
          } me-1`}
          style={{ cursor: "pointer", fontSize: "1.1rem" }}
          onClick={() => onChange && onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
        />
      ))}
      
    </div>
  );
};

export default ChatInterface;